import React, { useCallback, useId } from "react";
import { useTranslation } from "react-i18next";
import { useEffect, useRef } from "react";
import { Draw } from "ol/interaction";
import { DrawEvent } from "ol/interaction/Draw";
import { useMap } from "../../../hooks";
import { Button, ButtonProps } from "../Button";
import { MultiPointIcon } from "../../../constants/icons/MultiPointIcon";
import { useControlSection } from "../../layout";
import { InnerButton } from "../InnerButton";
import { positionsFromFeature } from "../../../../utils";
import { Coordinate } from "ol/coordinate";
import { makeGeojsonShape } from "../../../../utils/makeGeojsonShape";
import { FeatureFromGeojson, useFeaturesStore } from "../../../FeaturesStore";
import {
  useDrawSketchGestures,
  primaryPointerCondition,
} from "src/domains/shared/react-openlayers/Map/hooks/useDrawSketchGestures";
import { useToolGuide } from "../../useToolGuide";
import {
  DRAWING_FLAG_RELEASE_DELAY_MS,
  DRAWING_TOOL_CURSOR,
  DRAW_TOOL_EXIT_CURSOR,
  MAP_TOOL_BUTTON,
} from "src/domains/shared/constants/mapToolButton";

export interface MultiPointDrawButtonProps extends ButtonProps {
  /**
   * @description You can get Multipoint feature what was made by callback function.
   */
  onEnd?: (features: FeatureFromGeojson) => void;

  /**
   * @description You can set callback Fn on 'start' event.
   */
  onStart?: () => void;

  /**
   * @default false
   * @description Well... Sometimes you need this drawing tool with using server waht containes DB. if 'onCanvas' set false, react-openlayer will not draw feature on canvas.
   */
  onCanvas?: boolean;
}

export function MultiPointDrawButton({
  onEnd,
  onClick,
  onCanvas = false,
  onStart,
  disabled,
  ...props
}: MultiPointDrawButtonProps) {
  const { t } = useTranslation("timeSeries");
  const map = useMap();

  const id = useId();
  const buttonId = `controlbutton-${id}`;
  const { selectButton, selectedButtonId } = useControlSection();
  const isActive = buttonId === selectedButtonId;
  const { addGeoJson } = useFeaturesStore();

  const drawRef = useRef(
    new Draw({
      condition: primaryPointerCondition,
      source: undefined,
      type: "MultiPoint",
    })
  );

  const exitDrawing = useCallback(() => {
    selectButton("");
    map.setProperties({ isDrawing: false });
    map.getViewport().style.cursor = DRAW_TOOL_EXIT_CURSOR;
    map.removeInteraction(drawRef.current);
  }, [map, selectButton]);

  const { pointCount } = useDrawSketchGestures({
    map,
    isActive,
    kind: "point",
    getDraw: () => drawRef.current,
    onExit: exitDrawing,
  });
  useToolGuide({ isActive, kind: "point", pointCount });

  const startDrawing = () => {
    if (onClick) {
      onClick();
    }

    if (onStart) {
      onStart();
    }
    map.getViewport().style.cursor = DRAWING_TOOL_CURSOR;
    map.setProperties({ isDrawing: true });
    map.addInteraction(drawRef.current);
  };

  const drawing = useCallback(
    (event: DrawEvent) => {
      const feature = event.feature;
      const newPosition = positionsFromFeature(feature, true) as Coordinate[];

      const newGeoJson = makeGeojsonShape(
        {
          type: "MultiPoint",
          coordinates: newPosition,
        },
        {
          type: "multipoint",
          original_type: "multipoint",
          isSelected: true,
        }
      );
      selectButton("");
      map.getViewport().style.cursor = DRAW_TOOL_EXIT_CURSOR;
      drawRef.current.setActive(true);
      if (onEnd) {
        onEnd(newGeoJson);
      }
      if (onCanvas) {
        addGeoJson(newGeoJson);
      }

      setTimeout(
        () => map.setProperties({ isDrawing: false }),
        DRAWING_FLAG_RELEASE_DELAY_MS
      );
    },
    [selectButton, map, onEnd, onCanvas, addGeoJson]
  );

  useEffect(() => {
    const drawingInstance = drawRef.current;

    drawingInstance.on("drawend", drawing);

    return () => {
      drawingInstance.un("drawend", drawing);
    };
  }, [drawing]);

  useEffect(() => {
    if (!isActive) {
      map.removeInteraction(drawRef.current);
    }
  }, [isActive, map]);

  return (
    <Button
      isDisabled={disabled}
      disabled={disabled}
      id={buttonId}
      hasPopup
      popupText={t("mapTool.tool.multiMarker")}
      onClick={() => {
        if (!isActive) {
          startDrawing();
          selectButton(buttonId);
        } else {
          exitDrawing();
        }
      }}
      isActive={isActive}
      aria-pressed={isActive}
      {...props}
    >
      <InnerButton isActive={isActive}>
        <MultiPointIcon
          size={MAP_TOOL_BUTTON.ICON_PX}
          color={isActive ? MAP_TOOL_BUTTON.activeIcon : MAP_TOOL_BUTTON.idleIcon}
        />
      </InnerButton>
    </Button>
  );
}
